import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import type { TaskSummary } from "@/api/types/TaskSummary";
import { GitlabChip } from "@/components/GitlabChip";
import { ProgressBar, StatusPill, TestStageChip } from "@/components/Status";
import { AvatarStack, DueLabel, PriorityIcon, TaskCounters } from "@/components/work/Pickers";
import { relativeTime } from "@/lib/format";
import { recorded } from "@/lib/testing";
import { taskUrl } from "@/lib/work";

export { taskUrl };

/** One task as a card row (inbox, overview): key, title, status, testing progress, people. */
export function TaskRow({
  task,
  showProject,
  listSearch,
}: {
  task: TaskSummary;
  showProject?: boolean;
  /** The task list's filters (`?…`), so the task page can link back to the same filtered list. */
  listSearch?: string;
}) {
  const { t } = useTranslation();
  const round = task.latest_round;
  return (
    <Link
      to={taskUrl(task)}
      state={listSearch != null ? { tasksList: listSearch } : undefined}
      className="flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-3 text-text no-underline hover:bg-surface-2 hover:text-text hover:no-underline"
    >
      <div className="flex min-w-0 flex-[999_1_320px] flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-2">
          {showProject && <span className="font-mono text-xs text-muted">{task.project_key}</span>}
          {task.priority && <PriorityIcon priority={task.priority} labelled />}
          <span className="font-mono text-sm font-semibold text-accent">{task.key}</span>
          <StatusPill status={task.status} />
          <TestStageChip stage={task.test_stage} round={task.latest_round} hideNotStarted />
          <GitlabChip rollup={task.gitlab} />
          <DueLabel task={task} />
          <TaskCounters task={task} />
        </div>
        <span className="truncate font-medium">{task.title}</span>
      </div>

      <div className="flex w-[180px] flex-col gap-1.5">
        {round ? (
          <>
            <ProgressBar counts={round.counts} />
            <span className="text-xs text-muted">
              {t("tasks.roundN", { n: round.number })} · {recorded(round.counts)}/{round.counts.total}
              {round.counts.fail > 0 && <span className="text-fail-fg"> · {t("status.nFail", { count: round.counts.fail })}</span>}
            </span>
          </>
        ) : (
          <span className="text-xs text-muted">
            {t("tasks.caseCount", { count: task.case_count })} · {t("tasks.notTested")}
          </span>
        )}
      </div>

      <span className="flex w-[90px] justify-end">
        <AvatarStack users={task.assignees} />
      </span>

      <span className="w-[100px] text-right text-xs text-muted" title={task.updated_at}>
        {relativeTime(task.updated_at)}
      </span>
    </Link>
  );
}
